import React, { useRef, useState } from 'react';
import { useScheduleActionLock } from '@/hook/useScheduleActionLock';
import { toSmartScheduleKst } from '@/features/smartSchedule/model';
import {
    buildQuickMoveSavePlan, createQuickMoveDraft, findQuickMoveOverlaps,
    QuickMoveDraft, QuickMoveSaveNotice, QuickMoveSavePlan, QuickMoveSource,
    useQuickMoveDayPreview, useQuickMoveSave, validateQuickMoveDraft,
} from '@/features/scheduleQuickMove';

type Params = {
    source: QuickMoveSource;
    initialDraft?: QuickMoveDraft;
    centerId: string;
    authorization: string;
    onClose: () => void;
    onSuccess: (draft: QuickMoveDraft) => void;
    blockedMessage?: string;
};

const isAppliedVerification = (v?: string) => v === 'applied' || v === 'bothPresent';

/** 시트와 드래그 확인 창에서 같은 제출 잠금과 저장 결과 판정을 사용한다. */
export const useQuickMoveSubmission = ({ source, initialDraft, centerId, authorization, onClose, onSuccess, blockedMessage }: Params) => {
    const [editableDraft, setDraft] = useState(() => initialDraft || createQuickMoveDraft(source));
    const [notice, setNotice] = useState<QuickMoveSaveNotice | null>(null);
    const [submittedPlan, setSubmittedPlan] = useState<QuickMoveSavePlan>();
    const [finished, setFinished] = useState(false);
    // 교체로 생긴 새 자기 일정을 겹침에서 빼는 것은 서버 반영이 관측됐을 때만(applied·bothPresent).
    // 반영 안 됨·결과 미확인이면 목적지의 같은 회원 기존 중복 PT 를 새 일정으로 오인할 수 있다.
    const [observedApplied, setObservedApplied] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const lock = useRef(false);
    /*
     * 스케줄 동작 잠금(수정) — 끌어 옮기기·메뉴·시트 모두 이 제출을 지난다(설계 §6-7).
     * 메뉴에서 시트를 여는 길은 Provider 가 따로 묻고, 여기서는 **보내기 직전**에 다시 본다.
     */
    const actionLock = useScheduleActionLock('schedule.edit');
    const { save, isSaving: mutationSaving, heldNotice, heldPlan, heldVerification } = useQuickMoveSave({ centerId, authorization, scheduleId: source.scheduleId });
    const draft = heldPlan ? createQuickMoveDraft({ kind: source.kind, startTime: heldPlan.targetStartTime, endTime: heldPlan.targetEndTime }) : editableDraft;
    const displayNotice = notice || heldNotice;
    const isFinished = finished || !!heldNotice;
    const isSaving = submitting || mutationSaving;
    const validation = validateQuickMoveDraft(source, draft);
    const preview = useQuickMoveDayPreview({
        centerId, authorization, date: draft.date, coachId: source.coachId,
        enabled: !!draft.date && (validation.ok || validation.code !== 'INVALID_DATE'), excludeScheduleId: source.scheduleId,
    });
    const overlaps = validation.ok ? findQuickMoveOverlaps(source, validation, preview.events, isFinished ? (submittedPlan && observedApplied ? submittedPlan : isAppliedVerification(heldVerification) ? heldPlan : undefined) : undefined) : [];
    const overlapMessage = overlaps.length === 1
        ? `겹치는 일정이 있어요 · ${overlaps[0].title} ${toSmartScheduleKst(overlaps[0].startTime).format('HH:mm')}~${toSmartScheduleKst(overlaps[0].endTime).format('HH:mm')}`
        : `겹치는 일정이 ${overlaps.length}개 있어요`;
    const disabled = isSaving || isFinished || !!blockedMessage;
    const previewUnavailable = preview.isLoading || preview.isError || preview.isGroupError;
    const update = (next: QuickMoveDraft) => { if (!lock.current && !disabled) { setDraft(next); setNotice(null); setSubmittedPlan(undefined); } };
    const submit = async (event: React.FormEvent) => {
        event.preventDefault();
        if (lock.current || disabled || previewUnavailable || !validation.ok || validation.unchanged) return;
        if (actionLock.requiresCheck() && !(await actionLock.ensure())) return;
        // 인증을 기다리는 사이 다른 제출이 시작됐을 수 있다 — 다시 본다
        if (lock.current) return;
        lock.current = true;
        setSubmitting(true);
        try {
            const plan = buildQuickMoveSavePlan(source, validation);
            const result = await save(plan);
            if (result.type === 'success' || result.type === 'failure') {
                if (result.type === 'success' && result.verification === 'applied' && result.refetch === 'ok') {
                    onClose();
                    onSuccess(draft);
                    return;
                }
                setSubmittedPlan(plan);
                setObservedApplied(isAppliedVerification(result.verification));
                setNotice(result.notice);
                // 교체된 원본 또는 결과 미확인 스냅샷으로 재요청하지 않는다.
                setFinished(result.error?.outcomeUnknown || result.notice.showRefresh || result.verification !== 'notApplied' || result.refetch === 'failed'
                    || result.error?.code === 'STALE' || result.error?.code === 'NOT_FOUND');
            }
        } finally { lock.current = false; setSubmitting(false); }
    };
    const close = () => { if (!lock.current && !isSaving) onClose(); };

    return { draft, displayNotice, isFinished, isSaving, validation, preview, overlaps, overlapMessage, disabled, previewUnavailable, lock, update, submit, close, actionLock };
};
